import React, { useEffect, useState, MouseEvent } from 'react';
import { useApolloClient } from '@apollo/client';
import { connect } from 'react-redux';
import {
  createBookmarkMutation,
  deleteBookmarkMutation
} from '../../api/mutations';
import Bugsnag from '../../utils/bugsnag';
import { removeBookmarkFromCache } from '../../utils/helpers/updateBookmarkCache';
import FavoriteIcon from '../../public/static/images/item/favorite.svg';
import FavoriteFilledIcon from '../../public/static/images/item/filled-favorite.svg';
import Icon from '../Icon';
import { BookmarkArea, BookmarkContainer } from './styled';
import { setError } from '../../actions';

const mapDispatchToProps = dispatch => ({
  setErrorUI: (errorState: boolean) => dispatch(setError(errorState))
});

interface BookmarkProps {
  itemId: string;
  isActive?: boolean;
  isCurrentItemPage?: boolean;
  shouldUpdateCache?: boolean;
  removeItemFunc?: () => void;
  addBookmark?: () => void;
  deleteBookmark?: () => void;
  additionalClasses?: string;
  isGalleryHeader?: boolean;
  setFavorited?: (val: boolean) => void;
  stat?: string;
  isWebView?: boolean;
  renderCurrentIcons?: boolean;
  setErrorUI: (value: boolean) => void;
}

const BookmarkComponent: React.FC<BookmarkProps> = ({
  itemId,
  isActive = false,
  isCurrentItemPage = false,
  shouldUpdateCache = false,
  additionalClasses,
  isGalleryHeader = false,
  setFavorited,
  stat,
  isWebView = false,
  renderCurrentIcons = false,
  setErrorUI
}) => {
  const client = useApolloClient();
  const [active, setActive] = useState(isActive);

  useEffect(() => {
    setActive(isActive);
  }, [isActive]);

  const addBookmark = async (id: string) => {
    try {
      await client.mutate({
        variables: { itemId: id },
        mutation: createBookmarkMutation
      });
    } catch (error) {
      setErrorUI(true);
      Bugsnag.notify(error);
    }
  };

  const deleteBookmark = async (id: string) => {
    try {
      await client.mutate({
        variables: { itemId: id },
        mutation: deleteBookmarkMutation
      });
      if (shouldUpdateCache) removeBookmarkFromCache(id, client);
    } catch (error) {
      setErrorUI(true);
      Bugsnag.notify(error);
    }
  };

  const toggleBookmark = async (e: MouseEvent<HTMLAnchorElement>) => {
    e.preventDefault();
    const nextState = !active;

    if (setFavorited) {
      setFavorited(nextState);
    }

    setActive(nextState);

    if (nextState) {
      await addBookmark(itemId);
    } else {
      await deleteBookmark(itemId);
    }
  };

  const itemIdValue = itemId ? ` ${itemId}` : '';
  const ariaLabel = active
    ? `Remove ad${itemIdValue} from bookmark`
    : `Add ad${itemIdValue} to bookmark`;

  return (
    <BookmarkContainer
      className={additionalClasses}
      $isActive={active}
      $isWebView={isWebView}
      $isCurrentItemPage={isCurrentItemPage}
      $isGalleryHeader={isGalleryHeader}
      $renderCurrentIcons={renderCurrentIcons}
    >
      <BookmarkArea
        onClick={toggleBookmark}
        data-cy="bookmark-icon"
        data-stat={stat}
        data-action={active ? 'remove_bookmark' : 'add_bookmark'}
        role="button"
        aria-label={ariaLabel}
        $renderCurrentIcons={renderCurrentIcons}
      >
        {renderCurrentIcons && (
          <Icon
            IconComponent={active ? FavoriteFilledIcon : FavoriteIcon}
            width={22}
            height={22}
            indent={false}
          />
        )}
      </BookmarkArea>
    </BookmarkContainer>
  );
};

export const Bookmark = connect(null, mapDispatchToProps)(BookmarkComponent);
